import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getVoiceQueue, subscribeVoiceQueue } from '@/services/voice/queueStore';

/**
 * Expõe o "Ditar documentos" pra fora do PatientSessionPanel — a sidebar e o
 * botão flutuante do mobile (spec 009).
 *
 * Mesmo contrato do `ExtractionContext`: o painel é dono da lógica (abre o
 * modal, conhece o paciente) e só registra aqui o handler; a sidebar consome.
 *
 * `available` é DERIVADO da pilha de handlers vivos (mais o fallback da
 * página do paciente), nunca publicado à mão. Antes o painel publicava
 * `available: true` ao montar e ninguém zerava ao desmontar — a aba
 * Prontuário (a padrão) só monta um painel quando uma gaveta está aberta,
 * então depois da primeira visita à aba Entradas o botão ficava habilitado
 * SEM handler e o clique morria em silêncio até um Ctrl+Shift+R (visto em
 * campo, 18/09/2026).
 */

/** Handler de abertura: devolve `false` pra dizer "não consegui, tenta o próximo". */
export type DictationOpener = () => boolean | void;

/** De onde veio o pedido de abrir — vai pro diário e decide o bipe de pronto. */
export type DictationOrigin = 'clique' | 'tecla' | 'voz';

export interface DictationOpenOptions {
  /** Abre a coluna JÁ no "Ao vivo" (Alt+V / pedal; spec 011). */
  autoStart?: 'live';
  origem?: DictationOrigin;
  /** Se o ao vivo já estiver ligado, desliga (a mesma tecla liga e desliga). */
  toggle?: boolean;
}

/** Pedido de início automático, esperando a coluna abrir pra ser consumido. */
export interface DictationAutoStart {
  modo: 'live';
  origem: DictationOrigin;
  toggle: boolean;
}

interface DictationContextValue {
  /** Há alguém capaz de abrir o modal (painel montado ou fallback da ficha). */
  available: boolean;
  open: (opts?: DictationOpenOptions) => void;
  /**
   * A coluna que acabou de abrir pega o pedido de início automático (uma vez
   * só; vence em `AUTO_START_TTL_MS`). `null` = abertura comum.
   */
  takeAutoStart: () => DictationAutoStart | null;
  /** Alguma coluna do ditado está ouvindo (gravação, ao vivo ou conectando). */
  listening: boolean;
  /**
   * A coluna avisa que começou a ouvir; devolve o "parei". Contagem, não
   * booleano: a ficha monta várias colunas e uma recém-montada (parada) não
   * pode apagar o "ouvindo" da que está ao vivo. Use `useReportDictationListening`.
   */
  reportListening: () => () => void;
  /**
   * Instala o handler real. Devolve unsubscribe. `priority` maior vence
   * (o painel completo registra com 1; os "rail" do Prontuário com 0).
   * Se havia um clique pendente (ver `registerFallback`), o handler recém
   * registrado é chamado na hora.
   */
  registerOpen: (fn: DictationOpener, priority?: number) => () => void;
  /**
   * Plano B da página do paciente: chamado quando NENHUM painel está montado
   * (ex.: aba Prontuário sem gaveta aberta). Deve montar um painel — trocar
   * pra aba Entradas — e devolver `true`; o próximo `registerOpen` abre o
   * modal sozinho. Devolve unsubscribe.
   */
  registerFallback: (fn: () => boolean | void) => () => void;
}

const DictationContext = createContext<DictationContextValue | null>(null);

/** Clique pendente expira: um fallback que não montou painel nenhum não pode
 *  abrir o modal minutos depois, do nada. */
const PENDING_OPEN_TTL_MS = 5000;
/** Idem pro início automático: Alt+V fora da ficha não pode ligar o ao vivo
 *  quando o médico abrir a coluna minutos depois. */
const AUTO_START_TTL_MS = 5000;

interface Opener {
  fn: DictationOpener;
  priority: number;
  /** Zera no unsubscribe: entrada morta é pulada mesmo se sobrar na pilha. */
  alive: boolean;
}

export const DictationProvider = ({ children }: { children: ReactNode }) => {
  // PILHA, não um só: a ficha monta VÁRIOS PatientSessionPanel (o completo
  // e os "rail" do Prontuário). Com um único ref, quando o último a
  // registrar desmontava (trocar de aba), o handler virava null e o botão
  // "Ditar" da sidebar parava de abrir o modal (visto em campo, 17/09/2026).
  // Agora abre o mais recente que AINDA está montado.
  const openersRef = useRef<Opener[]>([]);
  const fallbacksRef = useRef<Array<() => boolean | void>>([]);
  const pendingUntilRef = useRef(0);
  // Contadores em estado só pra re-renderizar quem consome `available`.
  const [openerCount, setOpenerCount] = useState(0);
  const [fallbackCount, setFallbackCount] = useState(0);
  const [listening, setListening] = useState(false);
  const listeningCountRef = useRef(0);
  const reportListening = useCallback(() => {
    listeningCountRef.current += 1;
    setListening(true);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      listeningCountRef.current = Math.max(0, listeningCountRef.current - 1);
      setListening(listeningCountRef.current > 0);
    };
  }, []);
  const autoStartRef = useRef<(DictationAutoStart & { until: number }) | null>(null);

  const takeAutoStart = useCallback((): DictationAutoStart | null => {
    const req = autoStartRef.current;
    autoStartRef.current = null;
    if (!req || Date.now() > req.until) return null;
    return { modo: req.modo, origem: req.origem, toggle: req.toggle };
  }, []);

  const registerOpen = useCallback((fn: DictationOpener, priority = 0) => {
    const entry: Opener = { fn, priority, alive: true };
    openersRef.current = [...openersRef.current, entry];
    setOpenerCount(openersRef.current.length);
    // Clique que ficou esperando o painel montar (fallback trocou de aba).
    if (pendingUntilRef.current && Date.now() <= pendingUntilRef.current) {
      pendingUntilRef.current = 0;
      try {
        if (fn() === false) console.warn('[voice] handler recém-montado recusou abrir o ditado pendente');
      } catch (err) {
        console.warn('[voice] handler recém-montado falhou ao abrir o ditado pendente:', err);
      }
    }
    return () => {
      entry.alive = false;
      openersRef.current = openersRef.current.filter((e) => e !== entry);
      setOpenerCount(openersRef.current.length);
    };
  }, []);

  const registerFallback = useCallback((fn: () => boolean | void) => {
    fallbacksRef.current = [...fallbacksRef.current, fn];
    setFallbackCount(fallbacksRef.current.length);
    return () => {
      fallbacksRef.current = fallbacksRef.current.filter((f) => f !== fn);
      setFallbackCount(fallbacksRef.current.length);
    };
  }, []);

  const open = useCallback((opts?: DictationOpenOptions) => {
    // Gravado ANTES de chamar o handler: a coluna consome no efeito de abrir.
    autoStartRef.current = opts?.autoStart
      ? {
          modo: opts.autoStart,
          origem: opts.origem ?? 'clique',
          toggle: !!opts.toggle,
          until: Date.now() + AUTO_START_TTL_MS,
        }
      : null;
    // Maior prioridade primeiro; empate → o registrado mais recentemente.
    // Tenta um a um: handler morto (desmontou sem avisar), que devolve
    // `false` ou que lança é pulado — o clique nunca morre num handler só.
    const ordenados = [...openersRef.current]
      .reverse()
      .filter((e) => e.alive)
      .sort((a, b) => b.priority - a.priority);
    for (const e of ordenados) {
      try {
        if (e.fn() !== false) return;
        console.warn('[voice] handler recusou abrir (prioridade %d); tentando o próximo', e.priority);
      } catch (err) {
        console.warn('[voice] handler lançou ao abrir; tentando o próximo:', err);
      }
    }
    // Nenhum painel montado: pede à ficha pra montar um e deixa o clique
    // pendente pro próximo `registerOpen`.
    const fallback = fallbacksRef.current[fallbacksRef.current.length - 1];
    if (!fallback) {
      console.warn(
        '[voice] clique em "Ditar" sem handler vivo e sem fallback (%d na pilha, todos mortos) — abra a ficha do paciente (aba Entradas).',
        openersRef.current.length,
      );
      return;
    }
    pendingUntilRef.current = Date.now() + PENDING_OPEN_TTL_MS;
    console.info('[voice] nenhum painel montado; usando o fallback da ficha (troca de aba) com abertura pendente');
    try {
      if (fallback() === false) {
        pendingUntilRef.current = 0;
        console.warn('[voice] fallback recusou — o ditado não abriu');
      }
    } catch (err) {
      pendingUntilRef.current = 0;
      console.warn('[voice] fallback falhou — o ditado não abriu:', err);
    }
  }, []);

  const available = openerCount > 0 || fallbackCount > 0;

  const value = useMemo(
    () => ({ available, open, takeAutoStart, listening, reportListening, registerOpen, registerFallback }),
    [available, open, takeAutoStart, listening, reportListening, registerOpen, registerFallback],
  );

  return <DictationContext.Provider value={value}>{children}</DictationContext.Provider>;
};

/** Fora do provider devolve null — o chamador decide não renderizar. */
export function useOptionalDictation(): DictationContextValue | null {
  return useContext(DictationContext);
}

/** A coluna do ditado declara enquanto está ouvindo (a escuta da palavra de
 *  ativação se cala nesse tempo — spec 011). */
export function useReportDictationListening(active: boolean): void {
  const report = useContext(DictationContext)?.reportListening;
  useEffect(() => {
    if (!report || !active) return;
    return report();
  }, [report, active]);
}

/**
 * Documentos na fila do paciente — vira badge no botão. Lê direto do
 * `queueStore` (memória de módulo, por paciente), então não depende de
 * painel montado e nunca mostra a contagem de OUTRO paciente.
 */
export function useVoiceQueueCount(patientId: string | null): number {
  const [n, setN] = useState(() => (patientId ? getVoiceQueue(patientId).length : 0));
  useEffect(() => {
    if (!patientId) {
      setN(0);
      return;
    }
    setN(getVoiceQueue(patientId).length);
    return subscribeVoiceQueue((id) => {
      if (id === patientId) setN(getVoiceQueue(patientId).length);
    });
  }, [patientId]);
  return n;
}
